"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
exports.__esModule = true;
exports.AttrStore = void 0;
var globalHooks_1 = require("../../globalHooks");
var util_1 = require("../util");
var hooks_1 = require("./hooks");
var weakAttrStore_1 = require("./weakAttrStore");
var noopInvalidate = function () {
    util_1.warn("An invalidate function was not provided to an attrStore, meaning state updates will not propagate to the document.");
};
/**
 * Stores state associated with a single attribute on a single Node instance in the document.
 * An AttrStore works a little like a React component with hooks -- it holds a reference to a
 * function, and allows that function to be run over and over again, while holding onto stateful
 * context that it shares with that function via a set of "hooks" bound to `this` within the
 * function body. However, it does not store any state about the return result of the function --
 * that responsibility is left to the `NodeStore` which instantiates this class.
 *
 * Most of the methods of this class are public because they are designed to be accessed from
 * hooks bound to the instance at runtime -- they are not actually intended to be used by callers.
 */
var AttrStore = /** @class */ (function () {
    /**
     * @param attr The name of the attribute represented
     * @param fn The function which will be run to determine the attribute's value
     * @param documentHooks A set of document-scoped hooks to provide to `fn`
     * @param onInvalidate Callback for when a hook (cough cough, `useState`)
     *  invalidates this store's value
     */
    function AttrStore(attr, fn, documentHooks, onInvalidate) {
        this.cells = [];
        this.cellPointer = 0;
        this.destroyCallbacks = new Set();
        this.attr = attr;
        this.fn = fn;
        this.onInvalidate = onInvalidate || noopInvalidate;
        this.weakSelf = new weakAttrStore_1.WeakAttrStore(this);
        this.hooks = __assign(__assign({}, hooks_1.bindHooksToStore(this.weakSelf)), documentHooks);
    }
    AttrStore.prototype.invalidate = function () {
        this.onInvalidate(this.attr);
    };
    AttrStore.prototype.incrementPointer = function () {
        this.cellPointer++;
    };
    AttrStore.prototype.getCurrentCell = function () {
        if (!this.cells[this.cellPointer]) {
            this.cells[this.cellPointer] = [];
        }
        return this.cells[this.cellPointer];
    };
    AttrStore.prototype.registerRunCallback = function (callback) {
        this.runCallbacks.push(callback);
    };
    AttrStore.prototype.registerDestroyCallback = function (callback) {
        this.destroyCallbacks.add(callback);
    };
    AttrStore.prototype.unregisterDestroyCallback = function (callback) {
        this.destroyCallbacks["delete"](callback);
    };
    AttrStore.prototype.destroy = function () {
        this.weakSelf.destroy();
        __spread(this.destroyCallbacks).forEach(function (callback) { return callback(); });
    };
    /**
     * Given a `Node` instance, run `this.fn` on the node and return whatever we got.
     * @param node
     * @throws if hooks were called conditionally or out of order.
     */
    AttrStore.prototype.run = function (node) {
        this.runCallbacks = [];
        this.cellPointer = 0;
        globalHooks_1.setCurrentHooks(this.hooks);
        var result = this.fn(node);
        if (this.cellPointer !== this.cells.length) {
            util_1.throwError("Hooks called conditionally");
        }
        this.runCallbacks.forEach(function (callback) { return callback(); });
        return result;
    };
    return AttrStore;
}());
exports.AttrStore = AttrStore;
